/* import __COLOCATED_TEMPLATE__ from './calling.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type TwilioService from 'embercom/services/twilio-service';
import type VideoCallService from 'embercom/services/video-call-service';
import type Session from 'embercom/services/session';
import { action } from '@ember/object';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import { tracked } from '@glimmer/tracking';
import type Router from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'embercom/services/intl';
import {
  type ConferenceParticipantAddedEvent,
  type ConferenceParticipantRemovedEvent,
  type ConferenceParticipantFailedToJoinEvent,
  type WarmTransferAddedEvent,
  type WarmTransferFailedEvent,
  type WarmTransferCompletedEvent,
  type WarmTransferToTeamWaitingEvent,
  type WarmTransferToTeamCallingEvent,
  type WarmTransferToTeamFailedEvent,
  type InboundCallbackRequestEvent,
  NexusEventName,
} from 'embercom/services/nexus';

interface Args {}

interface Signature {
  Args: Args;
}

type SubscribedEvents =
  | InboundCallbackRequestEvent
  | ConferenceParticipantAddedEvent
  | ConferenceParticipantRemovedEvent
  | ConferenceParticipantFailedToJoinEvent
  | WarmTransferAddedEvent
  | WarmTransferFailedEvent
  | WarmTransferCompletedEvent
  | WarmTransferToTeamWaitingEvent
  | WarmTransferToTeamCallingEvent
  | WarmTransferToTeamFailedEvent;

export default class Calling extends Component<Signature> {
  readonly callingEvents = [
    NexusEventName.InboundCallbackRequest,
    NexusEventName.ConferenceParticipantAdded,
    NexusEventName.ConferenceParticipantRemoved,
    NexusEventName.ConferenceParticipantFailedToJoin,
    NexusEventName.WarmTransferAdded,
    NexusEventName.WarmTransferFailed,
    NexusEventName.WarmTransferCompleted,
    NexusEventName.WarmTransferToTeamWaiting,
    NexusEventName.WarmTransferToTeamCalling,
    NexusEventName.WarmTransferToTeamFailed,
  ];

  @service declare twilioService: TwilioService;
  @service declare videoCallService: VideoCallService;
  @service declare session: Session;
  @service declare intercomCallService: IntercomCallService;
  @service declare router: Router;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;

  @tracked confirmedNavigation = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (
      this.session.workspace &&
      (this.session.workspace.isFeatureEnabled('channels-phone-call') ||
        this.session.workspace.isFeatureEnabled('inbound-phone-call'))
    ) {
      this.intercomCallService.getWorkspacePhoneNumbers();
      this.twilioService.initialize();
    }
    this.router.on('routeWillChange', this.warnOfPossibleCallTermination);
  }

  get hasActiveOrPendingCall() {
    return (
      this.twilioService.isActiveCall ||
      this.twilioService.shouldNotifyTeammate ||
      this.twilioService.isListening ||
      this.videoCallService.isActiveCall ||
      this.videoCallService.isConnectingCall
    );
  }

  @action
  async warnOfPossibleCallTermination(transition: Transition) {
    if (!transition.to.name.startsWith('inbox.workspace')) {
      if (this.hasActiveOrPendingCall && !this.confirmedNavigation) {
        transition.abort();
        let confirmed = await this.intercomConfirmService.confirm({
          title: this.intl.t('calling.navigate-away-warning.title'),
          body: this.intl.t('calling.navigate-away-warning.body'),
          confirmButtonText: this.intl.t('calling.navigate-away-warning.confirm'),
          cancelButtonText: this.intl.t('calling.navigate-away-warning.cancel'),
        });
        if (confirmed) {
          this.confirmedNavigation = true;
          this.rejectPendingCall();
          if (this.videoCallService.isActiveCall) {
            this.videoCallService.endCall();
          } else if (this.videoCallService.isConnectingCall) {
            this.videoCallService.cancelConnectingCall();
          }
          transition.retry();
        }
      }
    }
  }

  willDestroy() {
    super.willDestroy();
    this.twilioService.unregisterDevice();
    this.intercomCallService.closeDialler();
  }

  @action handleCallingNexusEvent(event: SubscribedEvents) {
    if (this.session.teammate.id?.toString() !== event.eventData.adminId?.toString()) {
      return;
    }

    switch (event.eventName) {
      case NexusEventName.InboundCallbackRequest:
        this.twilioService.notifyInboundCallback(event);
        break;
      case NexusEventName.ConferenceParticipantAdded:
        this.twilioService.handleConferenceParticipantAdded(event);
        break;
      case NexusEventName.ConferenceParticipantRemoved:
        this.twilioService.handleConferenceParticipantRemoved(event);
        break;
      case NexusEventName.ConferenceParticipantFailedToJoin:
        this.twilioService.handleConferenceParticipantFailedToJoin(event);
        break;
      case NexusEventName.WarmTransferAdded:
        this.twilioService.handleWarmTransferAdded(event);
        break;
      case NexusEventName.WarmTransferFailed:
        this.twilioService.handleWarmTransferFailed(event);
        break;
      case NexusEventName.WarmTransferCompleted:
        this.twilioService.handleWarmTransferCompleted(event);
        break;
      case NexusEventName.WarmTransferToTeamWaiting:
        this.twilioService.handleWarmTransferToTeamWaiting();
        break;
      case NexusEventName.WarmTransferToTeamCalling:
        this.twilioService.handleWarmTransferToTeamCalling(event);
        break;
      case NexusEventName.WarmTransferToTeamFailed:
        this.twilioService.handleWarmTransferToTeamFailed(event);
        break;
    }
  }

  @action rejectPendingCall() {
    if (this.twilioService.shouldNotifyTeammate) {
      this.twilioService.rejectCall();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Calling': typeof Calling;
    'inbox2/calling': typeof Calling;
  }
}
